import { FormEvent, ReactNode, useState, useTransition } from "react";
import { toast } from "sonner";

import SaveBar from "@/components/save-bar";
import InfoTip from "@buildpy/ui/info-tip";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { SimpleSelect } from "@buildpy/ui/select";
import { Textarea } from "@buildpy/ui/textarea";
import { VAT_MODES, VatMode } from "@buildpy/core/modules/money/vat";
import { vatBasisLabel } from "@buildpy/core/modules/quote/snapshot-vat";
import { nameInputMax } from "@buildpy/core/modules/onboarding/rules";
import { parseSettingsCompanyName } from "@buildpy/core/modules/org/company-name";
import { COMPANY_NAME_MAX } from "@buildpy/core/modules/org/types";
import { settleRedirect } from "@/lib/redirect-error";
import { saveOrgSettings } from "@/api/actions/org";

export interface CompanySettings {
  company_name: string;
  /** 상호를 비워 저장했을 때 칸에 보일 org.name 대체값 — 새로고침 뒤와 같다. */
  company_name_fallback: string;
  company_address: string;
  company_tel: string;
  logo_url: string;
  stamp_url: string;
  default_conditions: string[];
  extra_notice: string;
  quote_valid_days: number;
  /** 화면은 % 로 다룬다. 저장은 액션이 비율로 바꾼다. */
  mgmt_fee_percent: number;
  quote_vat_mode: VatMode;
}

export const QUOTE_VAT_MODE_INFO_LABEL = "견적 단가 기본 안내";
const QUOTE_VAT_MODE_OPTIONS = VAT_MODES.map((mode) => ({ value: mode, label: vatBasisLabel(mode) }));

interface Props {
  value: CompanySettings;
  /** 실장은 읽기만 한다. 칸을 감추지 않고 잠근다 — 무엇이 설정돼 있는지는 봐야 한다. */
  canEdit: boolean;
}

/**
 * 회사 탭. 견적서에 그대로 찍히는 값들이라 한 폼에 모은다 —
 * 상호·연락처가 한 곳에 있고 견적 조건이 다른 곳에 있으면 발송 직전에 둘 다 열어야 한다.
 */
const SettingsCompany = ({ value, canEdit }: Props) => {
  const [pending, startTransition] = useTransition();
  const [dirty, setDirty] = useState(false);
  // 상호는 저장 액션과 같은 검사로 처음 로드·입력 중에 바로 판정한다(옛 값에 보이지 않는 문자가 있으면 칸에서 원인을 보인다 · 고칠 수 없는 읽기 전용엔 안 보인다).
  const [companyName, setCompanyName] = useState(value.company_name);
  const nameParsed = parseSettingsCompanyName(companyName);
  const nameError = canEdit && !nameParsed.ok ? nameParsed.error : "";

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const savedName = parseSettingsCompanyName(form.get("company_name"));

    startTransition(async () => {
      try {
        const result = await saveOrgSettings(form);
        if (result.ok) {
          setDirty(false);
          // 저장한 것은 정리값이다 — 비웠으면 org.name 대체값 · 새로고침 전에도 칸이 같은 값을 보이게 맞춘다.
          if (savedName.ok) setCompanyName(savedName.value || value.company_name_fallback);
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } catch (caught) {
        // 세션이 끊기면(401) settleRedirect 가 세션을 지우고 /login 으로 옮긴다 — 토스트로 삼키지 않는다.
        if (settleRedirect(caught)) return;
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <form className="flex flex-col gap-y-5" onSubmit={onSubmit}>
      <fieldset
        className="grid gap-4 md:grid-cols-2"
        disabled={!canEdit || pending}>
        <Field label="상호" name="company_name">
          <Input
            aria-describedby={nameError ? "company_name-error" : undefined}
            aria-invalid={nameError ? true : undefined}
            aria-labelledby="company_name-label"
            id="company_name"
            maxLength={nameInputMax(COMPANY_NAME_MAX)}
            name="company_name"
            onChange={(e) => {
              setCompanyName(e.target.value);
              setDirty(true);
            }}
            placeholder="예: 행복 인테리어"
            value={companyName}
          />
          {/* live 영역은 늘 있고 글만 바뀐다 · 비면 empty:-mt-1.5 로 칸 간격을 되돌린다 */}
          <p aria-live="polite" className="text-xs text-destructive empty:-mt-1.5" data-testid="company_name-error" id="company_name-error">
            {nameError}
          </p>
        </Field>

        <Field label="전화" name="company_tel">
          <Input
            defaultValue={value.company_tel}
            aria-labelledby="company_tel-label"
            id="company_tel"
            inputMode="tel"
            name="company_tel"
            onChange={() => setDirty(true)}
            placeholder="예: 02-000-0000"
            type="tel"
          />
        </Field>

        <Field className="md:col-span-2" label="주소" name="company_address">
          <Input
            defaultValue={value.company_address}
            aria-labelledby="company_address-label"
            id="company_address"
            name="company_address"
            onChange={() => setDirty(true)}
          />
        </Field>

        <Field
          hint="견적서 머리말에 들어갑니다"
          label="로고 URL"
          name="logo_url">
          <Input
            defaultValue={value.logo_url}
            aria-labelledby="logo_url-label"
            id="logo_url"
            name="logo_url"
            onChange={() => setDirty(true)}
            placeholder="https://"
          />
        </Field>

        <Field hint="인쇄본 날인 자리" label="도장 URL" name="stamp_url">
          <Input
            defaultValue={value.stamp_url}
            aria-labelledby="stamp_url-label"
            id="stamp_url"
            name="stamp_url"
            onChange={() => setDirty(true)}
            placeholder="https://"
          />
        </Field>

        <Field
          hint="공유 링크가 살아 있는 기간"
          label="견적 유효일 (일)"
          name="quote_valid_days">
          <Input
            defaultValue={value.quote_valid_days}
            aria-labelledby="quote_valid_days-label"
            id="quote_valid_days"
            inputMode="numeric"
            max={365}
            min={1}
            name="quote_valid_days"
            onChange={() => setDirty(true)}
            type="number"
          />
        </Field>

        <Field
          hint="새 견적의 기본값. 견적마다 따로 고칠 수 있습니다"
          label="관리비율 (%)"
          name="mgmt_fee_percent">
          <Input
            defaultValue={value.mgmt_fee_percent}
            aria-labelledby="mgmt_fee_percent-label"
            id="mgmt_fee_percent"
            inputMode="decimal"
            max={100}
            min={0}
            name="mgmt_fee_percent"
            onChange={() => setDirty(true)}
            step="0.1"
            type="number"
          />
        </Field>

        <Field
          hint="새 견적의 기본값. 견적마다 따로 고칠 수 있습니다"
          info={
            <InfoTip label={QUOTE_VAT_MODE_INFO_LABEL}>
              새 견적부터 적용됩니다. 이미 만든 견적은 그대로입니다.
            </InfoTip>
          }
          label="견적 단가 기본"
          name="quote_vat_mode">
          <SimpleSelect
            aria-label="견적 단가 기본 부가세"
            defaultValue={value.quote_vat_mode}
            id="quote_vat_mode"
            name="quote_vat_mode"
            onValueChange={() => setDirty(true)}
            options={QUOTE_VAT_MODE_OPTIONS}
          />
        </Field>

        <Field
          className="md:col-span-2"
          hint="한 줄에 하나씩. 새 견적의 견적 조건으로 들어갑니다"
          label="기본 견적조건"
          name="default_conditions">
          <Textarea
            className="min-h-32"
            defaultValue={value.default_conditions.join("\n")}
            aria-labelledby="default_conditions-label"
            id="default_conditions"
            name="default_conditions"
            onChange={() => setDirty(true)}
          />
        </Field>

        <Field
          className="md:col-span-2"
          hint="견적서 아래에 붙는 안내 문구"
          label="별도 부담금 안내"
          name="extra_notice">
          <Textarea
            className="min-h-24"
            defaultValue={value.extra_notice}
            aria-labelledby="extra_notice-label"
            id="extra_notice"
            name="extra_notice"
            onChange={() => setDirty(true)}
          />
        </Field>
      </fieldset>

      {canEdit ? (
        <SaveBar dirty={dirty} pending={pending} />
      ) : (
        <p className="text-sm text-muted-foreground">
          대표만 설정을 바꿀 수 있습니다.
        </p>
      )}
    </form>
  );
};

export default SettingsCompany;

const Field = ({
  label,
  name,
  hint,
  info,
  className,
  children,
}: {
  label: string;
  name: string;
  hint?: string;
  info?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <div className={`flex flex-col gap-y-1.5 ${className ?? ""}`}>
    {/* 칸마다 aria-labelledby 로도 잇는다 — for 연결만으로는 접근성 트리가 placeholder 를 이름으로 읽었다. */}
    {info ? (
      <div className="flex flex-wrap items-center gap-x-1">
        <Label htmlFor={name} id={`${name}-label`}>
          {label}
        </Label>
        {info}
      </div>
    ) : (
      <Label htmlFor={name} id={`${name}-label`}>
        {label}
      </Label>
    )}
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);
